import React from 'react';
import { Button, Heading, Text } from './ui';
import { useIOSWebView } from '../hooks/useIOSWebView';
import { hostOwnsExit } from '../utils/documentsHostBridge';
import './IOSHandoverError.css';

const COPY = {
  retryable: {
    title: "Couldn't connect",
    detail: 'Check your connection and try again.',
  },
  rejected: {
    title: 'Your session expired',
    detail: 'Go back and open it again.',
  },
};

/**
 * What a page the iOS app opened shows when it could not sign in with the
 * token the app handed it (`services/iosAuthHandover.js`). Never a web sign-in
 * form: signing in is the app's job.
 *
 * `kind` is `classifyHandoverError`'s answer. A 'retryable' failure offers Try
 * again, a reload that re-runs the handover with the token still in the URL. A
 * 'rejected' token can never succeed, so the only way on is back to the app;
 * reopening the page fetches a fresh token.
 *
 * Back asks the host to close this screen. It is offered only when the host
 * has an `exitToHost` handler: without one, `exitToHost` navigates inside the
 * WebView, and every page there needs the sign-in that just failed.
 */
const IOSHandoverError = ({ kind }) => {
  const { exitToHost } = useIOSWebView();
  const { title, detail } = COPY[kind];
  const canRetry = kind === 'retryable';
  const canGoBack = hostOwnsExit();

  return (
    <div className="ios-handover-error" role="alert">
      <div className="ios-handover-error__content">
        <Heading level={2} tone="dark" className="ios-handover-error__title">
          {title}
        </Heading>
        <Text
          tone="dark"
          variant="muted"
          size="sm"
          className="ios-handover-error__detail"
        >
          {detail}
        </Text>
        <div className="ios-handover-error__actions">
          {canRetry && (
            <Button
              variant="primary"
              tone="dark"
              onClick={() => window.location.reload()}
            >
              Try again
            </Button>
          )}
          {canGoBack && (
            <Button
              variant={canRetry ? 'secondary' : 'primary'}
              tone="dark"
              // Any destination but 'projects' closes the current screen.
              onClick={() => exitToHost({ destination: 'project' })}
            >
              Back
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IOSHandoverError;
